import { memo, useCallback } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, type SelectTriggerProps } from '@air/primitive-select';

import { usePrivateContextSourcesList } from '~/components/v2/hooks/queries/usePrivateContextSourcesList';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export type PrivateContextSourcesSelectProps = {
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
  size?: SelectTriggerProps['size'];
};

export const PrivateContextSourcesSelect = memo(
  ({
    value,
    onValueChange,
    disabled = false,
    id,
    placeholder = 'Choose a context source...',
    size = 'large',
  }: PrivateContextSourcesSelectProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { data: listData, isLoading } = usePrivateContextSourcesList(
      { workspaceId: currentWorkspace?.id },
      { enabled: !disabled },
    );
    const sources = listData?.data ?? [];

    const handleValueChange = useCallback(
      (nextValue: string) => {
        onValueChange(nextValue);
      },
      [onValueChange],
    );

    if (isLoading) {
      return (
        <Select disabled value="">
          <SelectTrigger disabled id={id} placeholder={placeholder} size={size} />
        </Select>
      );
    }

    return (
      <Select disabled={disabled} value={value} onValueChange={handleValueChange}>
        <SelectTrigger disabled={disabled} id={id} placeholder={placeholder} size={size} />
        <SelectContent>
          {sources.map((source) => (
            <SelectItem key={source.id} value={source.id}>
              {source.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  },
);

PrivateContextSourcesSelect.displayName = 'PrivateContextSourcesSelect';
